import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightProgress,
  SpotlightTitle,
  spotlightCloseButtonStyles,
} from '@heidi/design-system/spotlight';
import { X } from 'lucide-react';
import { useMemo, useRef, type ReactNode } from 'react';
import { defineMessage, useIntl } from 'react-intl';

const closeLabel = defineMessage({
  defaultMessage: 'Close',
  id: 'FtY7y34XjO',
  description: 'Accessible label for closing a Heidi II spotlight',
});

interface Heidi2SpotlightCardProps {
  /** The nav item the card points at; rendered unchanged. */
  children: ReactNode;
  title: ReactNode;
  description: ReactNode;
  actionLabel: ReactNode;
  onAction: () => void;
  onClose: () => void;
  /** "1/2" style position, for steps of a tour. */
  progress?: ReactNode;
  testId: string;
  /**
   * An element inside `children` to point at instead of the whole item —
   * for a rail item whose label sits under its icon, where the item's centre
   * falls between the two.
   */
  targetSelector?: string;
  /**
   * Radix only exempts the trigger from outside dismiss, not an Anchor. The
   * item passed as `children` and any portaled popper (its menus, flyouts and
   * hover previews) are exempted here; callers whose interactive content
   * reaches beyond both must preventDefault here for those targets, or a
   * click dismisses the tour.
   */
  onInteractOutside?: (event: Event) => void;
  /** A pointer press on the item passed as `children`, which never dismisses. */
  onItemPointerDown?: () => void;
}

/** Radix's event type for a pointer press outside the layer. */
const POINTER_DOWN_OUTSIDE = 'dismissableLayer.pointerDownOutside';

/** Where Radix portals every popper: dropdowns, flyouts, popovers. */
const POPPER_SELECTOR = '[data-radix-popper-content-wrapper]';

/**
 * The shared shape of the Heidi II nav spotlights: title, a close, a line of
 * copy, and one action, pointing right from a sidebar item.
 */
export const Heidi2SpotlightCard = ({
  children,
  title,
  description,
  actionLabel,
  onAction,
  onClose,
  progress,
  testId,
  targetSelector,
  onInteractOutside,
  onItemPointerDown,
}: Heidi2SpotlightCardProps) => {
  const intl = useIntl();
  const itemRef = useRef<HTMLDivElement>(null);
  const isPlaced = targetSelector !== undefined;
  // A measured stand-in for the anchor: horizontally the whole item,
  // vertically the target. Pointing from a rail icon alone would sit the card
  // on top of the rail. Measured on every read, so it tracks the item as the
  // sidebar settles.
  const placedRef = useMemo(
    () => ({
      current: {
        getBoundingClientRect: () => {
          const item = itemRef.current?.getBoundingClientRect();
          if (!item) return new DOMRect();
          const target = targetSelector
            ? itemRef.current?.querySelector(targetSelector)?.getBoundingClientRect()
            : undefined;
          return new DOMRect(
            item.left,
            target?.top ?? item.top,
            item.width,
            target?.height ?? item.height
          );
        },
      },
    }),
    [targetSelector]
  );

  const sideOffset = targetSelector ? 8 : 12;
  const alignOffset = targetSelector ? -12 : 0;

  const handleInteractOutside = (event: Event) => {
    const target = event.target;
    if (target instanceof Node && itemRef.current?.contains(target)) {
      event.preventDefault();
      if (event.type === POINTER_DOWN_OUTSIDE) onItemPointerDown?.();
      return;
    }
    // Menus opened from the item portal out of it; focus moving into one (on
    // open, and on every hovered option) must not end the tour.
    if (target instanceof Element && target.closest(POPPER_SELECTOR)) {
      event.preventDefault();
      return;
    }
    onInteractOutside?.(event);
  };

  return (
    <Spotlight
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {isPlaced ? (
        <>
          <div ref={itemRef}>{children}</div>
          <SpotlightAnchor virtualRef={placedRef} />
        </>
      ) : (
        <SpotlightAnchor ref={itemRef}>{children}</SpotlightAnchor>
      )}
      <SpotlightContent
        side="right"
        align="start"
        // Lift the card so the arrow sits below its rounded top corner, where
        // the design has it, rather than at the very top edge.
        alignOffset={alignOffset}
        // Against a rail item the arrow meets the rail's edge.
        sideOffset={sideOffset}
        // Re-pin every frame: these open straight after a navigation, while
        // the sidebar is still settling, and would otherwise land low.
        updatePositionStrategy="always"
        className="z-40 max-w-[calc(100vw-2rem)]"
        data-testid={testId}
        onInteractOutside={handleInteractOutside}
      >
        <SpotlightArrow />
        <SpotlightHeader>
          <SpotlightTitle>{title}</SpotlightTitle>
          <Button
            type="button"
            variant="ghost"
            size="xxs"
            iconOnly
            flush="all"
            onClick={onClose}
            className={spotlightCloseButtonStyles}
            aria-label={intl.formatMessage(closeLabel)}
            data-testid={`${testId}-close`}
          >
            <X />
          </Button>
        </SpotlightHeader>
        <SpotlightDescription className="text-pretty">{description}</SpotlightDescription>
        <SpotlightFooter
          className={progress ? 'items-center justify-between' : 'justify-end'}
        >
          {progress ? <SpotlightProgress>{progress}</SpotlightProgress> : null}
          <Button
            type="button"
            variant="secondary"
            size="xs"
            onClick={onAction}
            data-testid={`${testId}-action`}
          >
            {actionLabel}
          </Button>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
};
